import { useThemeSettingStorage } from "@/hooks/Storage/useThemeSettingStorage";
import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, RefObject, useCallback } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { IoClose } from "react-icons/io5";

const SIZE = {
  xs: "max-w-[300px]",
  sm: "max-w-[450px]",
  md: "max-w-[600px]",
  lg: "max-w-[750px]",
  xl: "max-w-[900px]",
};

interface ModalProps {
  title?: string | any;
  isShow: boolean;
  onClose?: () => void;
  onBack?: () => void;
  children: any;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  hideTopBar?: boolean;
  background?: string;
  isFooter?: any;
  hideClose?: boolean;
}

export default function Modal({
  title,
  isShow,
  onClose,
  onBack,
  children,
  className,
  size = "md",
  hideTopBar,
  background = "sm:bg-opacity-25 bg-bitzy-white dark:bg-bitzy-black",
  isFooter,
  hideClose,
}: ModalProps) {
  const { theme } = useThemeSettingStorage();
  const handleOnBack = useCallback(() => {
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  return (
    <Transition appear show={isShow} as={Fragment}>
      <Dialog
        as="div"
        className={clsx(theme, "relative z-[15]")}
        onClose={hideClose ? () => {} : handleOnClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={clsx("fixed inset-0", "bg-bitzy-black/10 backdrop-blur")}
          />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto overflow-x-hidden">
          <div
            className={clsx(
              "flex justify-center text-center",
              "min-h-dvh",
              "items-center p-4"
            )}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 translate-y-full"
              enterTo="opacity-100 translate-y-0"
              leave="ease-out duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-full"
            >
              <Dialog.Panel
                className={clsx(
                  "text-left align-middle transition-all transform",
                  "w-full",
                  "bg-bitzy-v2-gray/25 rounded-3xl border border-bitzy-v2-gray/100/15 backdrop-blur",
                  "shadow-none",
                  "inset-shadow",
                  SIZE[size]
                )}
              >
                <div className="flex flex-col max-h-[calc(100dvh_-_34px)]">
                  {!hideTopBar && (
                    <div
                      className={clsx(
                        "flex items-center p-4 sm:p-6",
                        title ? "justify-between" : "justify-end"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {onBack && (
                          <BsArrowLeft
                            className="cursor-pointer text-bitzy-black"
                            onClick={handleOnBack}
                          />
                        )}
                        {title && (
                          <span className="md:text-2xl text-[22px] font-bold text-bitzy-theme-btw">
                            {title}
                          </span>
                        )}
                      </div>
                      {hideClose ? (
                        <></>
                      ) : (
                        <IoClose
                          className="cursor-pointer text-bitzy-v2-white hover:text-bitzy-v2-white/70 transition-all duration-300"
                          size={22}
                          onClick={handleOnClose}
                        />
                      )}
                    </div>
                  )}

                  <div
                    className={clsx(
                      "flex w-full px-4 sm:px-6 mb-4 sm:mb-6 overflow-y-auto"
                    )}
                  >
                    {children}
                  </div>

                  {isFooter && <div className="mb-4 sm:mb-6">{isFooter}</div>}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
